import { useEffect, useState } from "react";
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Center,
  Checkbox,
  Container,
  Divider,
  Group,
  Loader,
  NumberInput,
  SegmentedControl,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconCircleCheck,
  IconInfinity,
  IconRefresh,
} from "@tabler/icons-react";
import {
  PayPalButtons,
  PayPalScriptProvider,
  usePayPalScriptReducer,
} from "@paypal/react-paypal-js";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSession } from "./auth/SessionContext";
import { steamLoginUrl } from "./auth/steamLogin";
import { useCart } from "./components/Cart/CartContext";
import { PAYPAL_CLIENT_ID, PAYPAL_SANDBOX } from "../config/paypal";
import { findPlan, PlanTier } from "../config/plans";

const APP = "lmutv";
type Edition = "standard" | "pro";
type Duration = "1d" | "3m" | "1y" | "lifetime";
type Status = "idle" | "saving" | "success" | "error" | "cancelled";

// First 3 PCs at full price, 4th+ at 50% — mirrors the PayPal TIERED plans (display
// only; PayPal is authoritative). Kept in sync with the tier setup.
const FULL_PRICE_PCS = 3;
const tieredTotal = (unit: number, qty: number): number =>
  Math.min(qty, FULL_PRICE_PCS) * unit + Math.max(0, qty - FULL_PRICE_PCS) * (unit / 2);

// 1-day plans exist only for renewal testing → offer them only in sandbox builds.
const DURATIONS: Duration[] = PAYPAL_SANDBOX
  ? ["1d", "3m", "1y", "lifetime"]
  : ["3m", "1y", "lifetime"];

// The PayPal subscription button + persistence (intent:"subscription"). Keyed by
// plan_id + quantity so switching reloads the button.
function SubscribeButton({
  plan,
  edition,
  duration,
  quantity,
  disabled,
  onStatus,
}: {
  plan: PlanTier;
  edition: Edition;
  duration: Duration;
  quantity: number;
  disabled: boolean;
  onStatus: (s: Status, msg?: string) => void;
}) {
  const [{ isPending }] = usePayPalScriptReducer();
  const { t } = useTranslation();

  if (isPending) {
    return (
      <Center py="md">
        <Loader color="lmuTVColors" />
      </Center>
    );
  }

  const persist = async (subscriptionId: string): Promise<boolean> => {
    const fd = new FormData();
    fd.append("subscription_id", subscriptionId);
    fd.append("app", APP);
    fd.append("edition", edition);
    fd.append("duration", duration);
    fd.append("quantity", String(quantity));
    fd.append("affiliate", localStorage.getItem("ref") ?? "");
    try {
      const r = await fetch("/dyn/save-subscription.php", { method: "POST", body: fd });
      const j = await r.json();
      return j.success === true;
    } catch {
      return false;
    }
  };

  return (
    <div style={{ position: "relative" }}>
      <PayPalButtons
        key={`${plan.paypalPlanId}-${quantity}`}
        style={{ layout: "vertical", color: "gold", shape: "rect", label: "subscribe" }}
        disabled={disabled}
        createSubscription={(_data, actions) =>
          actions.subscription.create({
            plan_id: plan.paypalPlanId as string,
            quantity: String(quantity),
          })
        }
        onApprove={async (data) => {
          onStatus("saving");
          const subId = data.subscriptionID ?? "";
          const ok = subId ? await persist(subId) : false;
          onStatus(ok ? "success" : "error");
        }}
        onError={() => onStatus("error", t("subscribe.errorMessage"))}
        onCancel={() => onStatus("cancelled")}
      />
      {disabled && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            backdropFilter: "blur(3px)",
            backgroundColor: "rgba(0,0,0,0.15)",
            borderRadius: 4,
            cursor: "not-allowed",
            zIndex: 10,
          }}
        />
      )}
    </div>
  );
}

export function LmuTvSubscribe() {
  const { t } = useTranslation();
  const { sessionData } = useSession();
  const cart = useCart();
  const [params] = useSearchParams();

  const [edition, setEdition] = useState<Edition>("standard");
  const [duration, setDuration] = useState<Duration>("3m");
  const [quantity, setQuantity] = useState(1);
  const [tosAccepted, setTosAccepted] = useState(false);
  const [status, setStatus] = useState<Status>("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Preselect from the storefront buy buttons (/subscribe?edition=&duration=&pcs=).
  useEffect(() => {
    const e = params.get("edition");
    const d = params.get("duration");
    const p = params.get("pcs");
    if (e === "standard" || e === "pro") setEdition(e);
    if (d && DURATIONS.includes(d as Duration)) setDuration(d as Duration);
    if (p) setQuantity(Math.max(1, parseInt(p, 10) || 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const plan = findPlan(APP, edition, duration);
  const loggedIn = !!sessionData?.logged_in;
  const isSub = plan?.type === "subscription";

  const onStatus = (s: Status, msg?: string) => {
    setStatus(s);
    setErrorMsg(msg ?? null);
  };

  const intervalLabel =
    duration === "3m"
      ? t("subscribe.interval.3m")
      : duration === "1y"
      ? t("subscribe.interval.1y")
      : "täglich";

  const total = plan ? tieredTotal(plan.price, quantity) : 0;
  const showBulk = quantity > FULL_PRICE_PCS;

  return (
    <Container size="sm" py="xl">
      <Stack gap="lg">
        <div>
          <Badge color="lmuTVColors" size="lg" mb="sm">
            LMU TV
          </Badge>
          <Title order={2}>{t("subscribe.title")}</Title>
          <Text c="dimmed" mt="xs">
            {t("subscribe.subtitle")}
          </Text>
        </div>

        {status === "success" && (
          <Alert icon={<IconCircleCheck size={20} />} title={t("subscribe.successTitle")} color="green" radius="md">
            {t("subscribe.successMessage")}
          </Alert>
        )}
        {status === "error" && (
          <Alert icon={<IconAlertCircle size={20} />} title={t("subscribe.errorTitle")} color="red" radius="md" withCloseButton onClose={() => onStatus("idle")}>
            {errorMsg ?? t("subscribe.errorMessage")}
          </Alert>
        )}
        {status === "cancelled" && (
          <Alert title={t("subscribe.cancelledTitle")} color="yellow" radius="md" withCloseButton onClose={() => onStatus("idle")}>
            {t("subscribe.cancelledMessage")}
          </Alert>
        )}

        <Card shadow="sm" radius="md" padding="lg" withBorder>
          <Stack gap="md">
            {/* Edition */}
            <div>
              <Text size="sm" fw={600} mb={4}>
                {t("subscribe.edition.label")}
              </Text>
              <SegmentedControl
                fullWidth
                value={edition}
                onChange={(v: string) => setEdition(v as Edition)}
                data={[
                  { value: "standard", label: t("subscribe.edition.standard") },
                  { value: "pro", label: t("subscribe.edition.pro") },
                ]}
              />
            </div>

            {/* Duration */}
            <div>
              <Text size="sm" fw={600} mb={4}>
                {t("subscribe.duration.label")}
              </Text>
              <SegmentedControl
                fullWidth
                value={duration}
                onChange={(v: string) => setDuration(v as Duration)}
                data={DURATIONS.map((d) => ({
                  value: d,
                  label: d === "1d" ? "1 Tag (Test)" : t(`subscribe.duration.${d}`),
                }))}
              />
            </div>

            {/* PCs (quantity) — only for subscriptions */}
            {isSub && (
              <NumberInput
                label={t("subscribe.pcs.label")}
                description={t("subscribe.pcs.hint")}
                min={1}
                max={50}
                value={quantity}
                onChange={(v) => setQuantity(typeof v === "number" ? v : parseInt(String(v), 10) || 1)}
                allowDecimal={false}
                clampBehavior="strict"
              />
            )}

            <Divider />

            {/* Price + note */}
            <Group justify="space-between" align="baseline">
              <Text size="sm" c="dimmed">
                {t("subscribe.price")}
              </Text>
              <Text fw={700} size="xl">
                {plan ? `${(isSub ? total : plan.price).toFixed(2)}€` : "—"}
              </Text>
            </Group>

            {isSub ? (
              <>
                <Text size="xs" c="dimmed">
                  <IconRefresh size={12} style={{ verticalAlign: "middle" }} />{" "}
                  {t("subscribe.recurringNote", { interval: intervalLabel })}
                </Text>
                {showBulk && (
                  <Text size="xs" c="lmuTVColors.4">
                    {t("subscribe.bulkNote")}
                  </Text>
                )}
              </>
            ) : (
              <Text size="xs" c="dimmed">
                <IconInfinity size={12} style={{ verticalAlign: "middle" }} /> {t("subscribe.lifetimeNote")}
              </Text>
            )}

            <Divider />

            {/* Checkout */}
            {!loggedIn ? (
              <Button component="a" href={steamLoginUrl()} color="lmuTVColors" fullWidth>
                {t("subscribe.loginRequired")}
              </Button>
            ) : !isSub ? (
              // Lifetime = one-time purchase → open the cart with this selection.
              <Button
                variant="light"
                color="lmuTVColors"
                fullWidth
                onClick={() => cart?.openCart({ edition, duration: "lifetime", pcs: quantity, autoAdd: true })}
              >
                {t("subscribe.lifetimeCta")}
              </Button>
            ) : !plan || !plan.paypalPlanId ? (
              <Alert color="yellow" radius="md">
                {t("subscribe.notAvailable")}
              </Alert>
            ) : (
              <>
                <Checkbox
                  checked={tosAccepted}
                  onChange={() => setTosAccepted((v) => !v)}
                  label={
                    <Text size="xs">
                      {t("subscribe.tos.prefix")}{" "}
                      <Anchor href="/tos" target="_blank" size="xs">
                        {t("subscribe.tos.tos")}
                      </Anchor>{" "}
                      {t("subscribe.tos.and")}{" "}
                      <Anchor href="/privacy_web" target="_blank" size="xs">
                        {t("subscribe.tos.privacy")}
                      </Anchor>
                    </Text>
                  }
                />
                <PayPalScriptProvider
                  options={{ clientId: PAYPAL_CLIENT_ID, vault: true, intent: "subscription" }}
                >
                  <SubscribeButton
                    plan={plan}
                    edition={edition}
                    duration={duration}
                    quantity={quantity}
                    disabled={!tosAccepted || status === "saving"}
                    onStatus={onStatus}
                  />
                </PayPalScriptProvider>
              </>
            )}
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
}
